/**
 * Insights — the pure half. No React, no fetch, no dates-from-now.
 *
 * ===========================================================================
 * THE TONE RULE, WRITTEN DOWN WHERE IT CAN BE TESTED
 * ===========================================================================
 * "The UI must encourage investigation, not anxiety. No giant red alert wall.
 * WATCH EVERYTHING. NOTIFY VERY LITTLE."
 *
 * That is a design instruction and design instructions decay, so the parts of
 * it that CAN be executable are executable and are pinned by
 * `insights-tone.test.ts`:
 *
 *   - `toneFor` gives `info` and `warning` QUIET treatments. Only `critical`
 *     gets an emphatic one, and critical is a band the detectors reach only
 *     when something has already lapsed — see `sources.ts`, where the
 *     expiring-credential detector's `maxSeverity` is 'warning' by
 *     construction, so "expires in four days" cannot render red however close
 *     it gets.
 *   - `VIEWS` puts **Needs attention** first and **Dismissed** last, and the
 *     view labels are sentences about attention rather than about alarm.
 *   - There is no `count` badge on a view tab. A number beside a tab is a
 *     standing instruction to make it go down, and this queue is not a
 *     to-do list — most findings are things to know, not things to clear.
 */

import { dataHealthCaveat, type DataHealthCheck } from '../dataHealth.js';
import type {
  FindingApplicability,
  FindingApplicabilityState,
  FindingEntity,
  FindingSeverity,
  FindingState,
} from './insightsClient.js';

// ---------------------------------------------------------------------------
// The five views
// ---------------------------------------------------------------------------

export interface InsightView {
  readonly key: FindingState;
  readonly label: string;
  /** One quiet line under the tab row saying what this view is. */
  readonly descriptor: string;
  /** The empty state for THIS view. Five views, five different zeros. */
  readonly empty: string;
}

export const VIEWS: readonly InsightView[] = [
  {
    key: 'needs_attention',
    label: 'Needs attention',
    descriptor: 'Noticed, and nobody has looked yet.',
    empty: 'Nothing is waiting on a first look.',
  },
  {
    key: 'watching',
    label: 'Watching',
    descriptor: 'Someone has looked. Not resolved, not unattended.',
    empty: 'Nothing is being watched right now.',
  },
  {
    key: 'resolved',
    label: 'Resolved',
    // NOT "The condition stopped being true."
    //
    // That descriptor was written when a resolution could only mean one thing.
    // Since the 2026-09-08 closeout ruling it can also mean Phibian withdrew an
    // obligation it should never have asserted — and this line sits above the
    // list, so it would have asserted, over every withdrawn finding, precisely
    // the reading the ruling forbids. The tab still holds both, because both
    // are closed; the line no longer says which happened, and the history on
    // each finding does.
    descriptor:
      'Closed by Phibian. Open one to see whether the condition ended or the obligation was withdrawn.',
    empty: 'Nothing has been closed yet.',
  },
  {
    key: 'dismissed',
    label: 'Dismissed',
    descriptor: 'Somebody decided it did not matter, and said why.',
    empty: 'Nothing has been dismissed.',
  },
  {
    // LAST, AND NOT A SYNONYM FOR DISMISSED.
    //
    // "Dismissed" is somebody saying this does not matter today, and the next
    // sweep brings it back if the condition returns. "Not applicable" is
    // somebody with standing saying the requirement does not govern this
    // program — scoped, sourced, and not undone by the condition returning.
    // The two tabs are adjacent so the difference is legible; the descriptors
    // are the whole explanation a person gets, so they say the different thing
    // rather than two shades of the same one.
    key: 'not_applicable',
    label: 'Not applicable',
    descriptor: 'A recorded determination says the requirement does not govern this.',
    empty: 'Nothing has been ruled inapplicable.',
  },
];

export const DEFAULT_VIEW: FindingState = 'needs_attention';

export function viewFor(raw: string | undefined): InsightView {
  return VIEWS.find((view) => view.key === raw) ?? VIEWS[0]!;
}

// ---------------------------------------------------------------------------
// Tone
// ---------------------------------------------------------------------------

export type Tone = 'quiet' | 'notice' | 'urgent';

/**
 * Severity → tone, and the mapping is deliberately not one-to-one.
 *
 * `info` and `warning` both read QUIET. The visual difference between "worth
 * knowing" and "worth doing something about soon" is a word, not a colour: a
 * page that paints every warning amber is the alert wall with a softer
 * palette. Only `critical` is emphatic, and only `critical` earns it.
 */
export function toneFor(severity: FindingSeverity): Tone {
  switch (severity) {
    case 'critical':
      return 'urgent';
    case 'warning':
      return 'notice';
    case 'info':
      return 'quiet';
  }
}

/**
 * Tone → the design system's own severity vocabulary.
 *
 * NOTE WHAT `warning` MAPS TO. `look`, not `urgent` — the token family whose
 * whole job is "worth a glance". The alert wall the founder forbade is what
 * you get by mapping warning to the urgent family because it is called
 * "warning"; the mapping is here, in one testable function, so that decision
 * is visible rather than scattered across two components' JSX.
 */
export function pillVariantFor(severity: FindingSeverity): 'plain' | 'look' | 'urgent' {
  switch (toneFor(severity)) {
    case 'urgent':
      return 'urgent';
    case 'notice':
      return 'look';
    case 'quiet':
      return 'plain';
  }
}

/** The word a reader sees. Never "ALERT", never an exclamation mark. */
export function severityLabel(severity: FindingSeverity): string {
  switch (severity) {
    case 'critical':
      return 'Needs action';
    case 'warning':
      return 'Worth a look';
    case 'info':
      return 'For information';
  }
}

// ---------------------------------------------------------------------------
// Entities — the four states, rendered as four sentences
// ---------------------------------------------------------------------------

/**
 * What a reader is told about an entity a finding names.
 *
 * WITHHELD IS NOT ABSENT AND NEITHER IS BLANK. The server answers three
 * different things and this function keeps them three different sentences;
 * collapsing them is the defect the lane contract ranks worst, and on this
 * screen the collapse would read as "this finding is about nothing".
 */
export function entitySentence(entity: FindingEntity): string {
  switch (entity.disclosure) {
    case 'shown':
      return entity.label;
    case 'withheld':
      return 'Not shown to you';
    case 'absent':
      return 'No longer in the records';
  }
}

/** The subject entities, which are what the finding is ABOUT. */
export function subjectsOf(entities: readonly FindingEntity[]): FindingEntity[] {
  return entities.filter((entity) => entity.role === 'subject');
}

export function contextOf(entities: readonly FindingEntity[]): FindingEntity[] {
  return entities.filter((entity) => entity.role === 'context');
}

// ---------------------------------------------------------------------------
// The population frame
// ---------------------------------------------------------------------------

/**
 * The sentence under the heading, read OFF THE RESPONSE.
 *
 * `organizationWide` is a server fact on the payload. The screen states the
 * frame the response stated and reconstructs nothing — re-deriving it from
 * the caller's permissions is how a narrowed list comes to be labelled
 * organization-wide.
 */
export function frameSentence(organizationWide: boolean | undefined): string {
  if (organizationWide === undefined) {
    return 'What Phibian noticed in the records your account can see. We could not check how far that reaches.';
  }
  return organizationWide
    ? 'What Phibian noticed across your organization.'
    : 'What Phibian noticed among the people you supervise. Someone with organization-wide access may see more.';
}

/**
 * The empty-state sentence for a view, honest about whose zero it is —
 * AND, on `needs_attention`, honest about whether the data-health cross-check
 * says there may be more to notice than the sweep has looked at yet.
 *
 * "Nothing is waiting on a first look" is a completeness claim about
 * Phibian's own noticing, the same species as Needs Attention's trust
 * sentence (`empty.successAttention`) — and the same cross-check applies:
 * `domains/rules` (which is what raises findings) has never once referenced
 * `domains/integrations`, so a genuinely empty `needs_attention` view could
 * still sit beside a connector whose historical import has not finished, and
 * nothing here would say so. Only `needs_attention` gets the caveat — the
 * other three views (`watching`, `resolved`, `dismissed`) are not claims that
 * nothing is missing, so a data-health check has nothing to qualify there.
 *
 * THREE-VALUED, NOT TWO (PH-ATT-001). `dataHealthCheck` used to be
 * `{message} | null | undefined` — a shape with no way to say "the check did
 * not answer" — so a loading or failed cross-check rendered the SAME
 * unqualified "nothing is waiting" as a genuinely clear one. `dataHealthCaveat`
 * treats 'unknown' exactly like 'gap' here: both withhold the plain claim,
 * they only differ in which sentence replaces it.
 */
export function emptySentence(
  view: InsightView,
  organizationWide: boolean | undefined,
  dataHealthCheck?: DataHealthCheck,
): string {
  const caveat = dataHealthCheck ? dataHealthCaveat(dataHealthCheck) : undefined;
  if (view.key === 'needs_attention' && caveat) {
    const scoped =
      organizationWide === false ? ' Someone with organization-wide access may see more.' : '';
    return `Nothing has been flagged yet — but ${caveat}${scoped}`;
  }
  if (organizationWide === false) {
    return `${view.empty} Someone with organization-wide access may see more.`;
  }
  return view.empty;
}

// ---------------------------------------------------------------------------
// Recurrence
// ---------------------------------------------------------------------------

/**
 * "Seen once" is different from "seen fourteen times", and the second is the
 * more interesting fact — a condition that keeps coming back is usually a
 * process problem rather than an incident.
 */
export function recurrenceSentence(count: number): string {
  if (count <= 1) return 'First time';
  return `Seen ${count} times`;
}

// ---------------------------------------------------------------------------
// Applicability, in sentences
// ---------------------------------------------------------------------------

/**
 * WHY IS THIS SILENT — one sentence per answer, and the three arms stay three.
 *
 * `undetermined` is NOBODY HAS DECIDED. It is not `unknown`, which is a person
 * who looked and could not finish, and rendering the first as the second would
 * put an assessment on the screen that nobody made. Both sentences say which
 * of the two it is in words, because the reader has no other way to tell.
 *
 * `conflict` is deliberately NOT resolved to a winner here. The server does not
 * invent a precedence and neither does the screen; the honest sentence is that
 * two determinations disagree and somebody has to reconcile them.
 */
export function applicabilitySentence(applicability: FindingApplicability): string {
  switch (applicability.status) {
    case 'undetermined':
      return 'Nobody has recorded whether this requirement governs this program.';
    case 'requires_reconciliation':
      return 'Two or more recorded determinations disagree about whether this governs here. Phibian is not choosing between them; this needs reconciling.';
    case 'determined':
      return APPLICABILITY_SENTENCES[applicability.disposition];
  }
}

const APPLICABILITY_SENTENCES: Record<FindingApplicabilityState, string> = {
  applies: 'A recorded determination says this requirement governs here.',
  does_not_apply: 'A recorded determination says this requirement does not govern here.',
  // The founder's own sentence, near enough to verbatim. It is the one answer
  // most likely to be mistaken for "does not apply", so it says what is
  // missing rather than what was concluded.
  unknown:
    'Applicability could not be determined because provider, program or service classification is incomplete. This is not the same as “does not apply”.',
  requires_reconciliation:
    'Recorded determinations disagree about whether this governs here. This needs reconciling.',
  not_yet_effective: 'This requirement does not govern here yet. It will, on the recorded date.',
  historical: 'This requirement governed here once and has been superseded.',
};

/**
 * EVENT KINDS IN WORDS.
 *
 * The history used to render `event.kind` raw, so a reader met `owner_assigned`
 * as `owner_assigned`. That was survivable while every kind was a single
 * ordinary word; it stops being survivable with `applicability_determined`
 * beside `applicability_lapsed`, which are the two whose difference a reader
 * most needs and which look nearly identical in snake_case.
 *
 * An unmapped kind falls back to the raw token rather than to a generic
 * "changed": a token a reader can search for is worse-looking and more
 * truthful than a label that hides which event it was.
 */
const EVENT_LABELS: Record<string, string> = {
  discovered: 'Noticed',
  recurred: 'Seen again',
  acknowledged: 'Acknowledged',
  dismissed: 'Dismissed',
  resolved: 'Resolved',
  reopened: 'Condition returned',
  owner_assigned: 'Owner assigned',
  note: 'Note',
  applicability_determined: 'Ruled not applicable',
  applicability_lapsed: 'Applicability determination ended',
};

/**
 * A RESOLUTION IS NOT ONE WORD, AND "Resolved" IS THE WRONG WORD FOR TWO OF
 * THEM.
 *
 * The founder's closeout ruling of 2026-09-08 (#2) withdrew the fabricated
 * 45-day supervision requirement and required Phibian to reconcile the findings
 * it had produced — while forbidding the reconciliation from being represented
 * as "a worker becoming compliant; a manager resolving the problem; the
 * organization satisfying a requirement; evidence that supervision occurred".
 *
 * The timeline caption is where three of those four readings would arrive.
 * `finding_event.resolution_reason` is what tells them apart, so the caption
 * reads it. Note what each label refuses to say:
 *
 *   - the withdrawal names PHIBIAN as the actor and the OBLIGATION as the thing
 *     that changed. No word in it is about the worker.
 *   - the unevaluable one says the sweep closed the finding WITHOUT deciding
 *     it, so a reader cannot take it for either of the other two.
 *
 * `null` — a resolution written before the distinction existed — keeps the
 * plain word, because for those rows we genuinely do not know which it was and
 * a confident new caption would be a claim nobody measured.
 */
const RESOLUTION_LABELS: Record<string, string> = {
  condition_no_longer_observed: 'Resolved',
  obligation_withdrawn_no_configured_requirement: 'Obligation withdrawn by Phibian',
  subject_no_longer_evaluable: 'Closed without a decision',
};

export function eventLabel(kind: string, resolutionReason?: string | null): string {
  if (kind === 'resolved' && resolutionReason != null) {
    return RESOLUTION_LABELS[resolutionReason] ?? resolutionReason;
  }
  return EVENT_LABELS[kind] ?? kind;
}
